<template>
  <div>
    <b-container class="mt-5 mb-5">

      <!-- HEADER -->
      <div class="d-flex justify-content-between align-items-center mb-4">
        <h1 class="fs-4 font-weight-bold mb-0">Klantstatistieken</h1>

        <b-button-group size="sm">
          <b-button
            v-for="range in ranges"
            :key="range.value"
            :variant="selectedRange === range.value ? 'primary' : 'outline-secondary'"
            @click="selectedRange = range.value"
          >
            {{ range.label }}
          </b-button>
        </b-button-group>
      </div>

      <!-- LOADING -->
      <div v-if="loading" class="text-center py-5">
        <b-spinner small></b-spinner>
      </div>

      <div v-else-if="stats">

        <!-- KPI OVERZICHT -->
        <b-row class="mb-4">
          <b-col md="3" v-for="(kpi, index) in kpis" :key="index">
            <b-card class="text-center kpi-card">
              <h6 class="text-muted">{{ kpi.label }}</h6>
              <h2 class="font-weight-bold">{{ kpi.value }}</h2>
            </b-card>
          </b-col>
        </b-row>

        <!-- KOOPGEDRAG -->
        <b-card no-body class="mb-4">
          <b-card-header>
            <h3 class="fs-5 mb-0">Aankoopmoment per uur</h3>
          </b-card-header>
          <b-card-body>
            <div class="mini-bar-chart">
              <div
                v-for="(hour, index) in hourlySales"
                :key="index"
                class="mini-bar"
              >
                <div
                  class="mini-bar-fill"
                  :style="{ height: hour.percent + '%' }"
                ></div>
                <div class="mini-bar-label">
                  {{ hour.label }}
                </div>
              </div>
            </div>
          </b-card-body>
        </b-card>

        <!-- DOELGROEP -->
        <b-row class="mb-4">
          <b-col md="6">
            <b-card no-body>
              <b-card-header>
                <h3 class="fs-5 mb-0">Leeftijdsverdeling</h3>
              </b-card-header>
              <b-card-body>
                <div v-for="age in ageStats" :key="age.label" class="mb-3">
                  <div class="d-flex justify-content-between">
                    <span>{{ age.label }}</span>
                    <strong>{{ age.percent }}%</strong>
                  </div>
                  <b-progress :value="age.percent" height="15px" />
                </div>
              </b-card-body>
            </b-card>
          </b-col>

          <b-col md="6">
            <b-card no-body>
              <b-card-header>
                <h3 class="fs-5 mb-0">Genderverdeling</h3>
              </b-card-header>
              <b-card-body>
                <div v-for="gender in genderStats" :key="gender.label" class="mb-3">
                  <div class="d-flex justify-content-between">
                    <span>{{ gender.label }}</span>
                    <strong>{{ gender.percent }}%</strong>
                  </div>
                  <b-progress :value="gender.percent" height="15px" />
                </div>
              </b-card-body>
            </b-card>
          </b-col>
        </b-row>

        <!-- INSIGHTS -->
        <b-card no-body class="mb-4">
          <b-card-header>
            <h3 class="fs-5 mb-0">Inzichten</h3>
          </b-card-header>

          <b-card-body>
            <b-row>
              <b-col md="4" class="mb-3">
                <div class="insight-card">
                  <div class="insight-icon">👥</div>
                  <div>
                    <div class="insight-title">Grootste doelgroep</div>
                    <div class="insight-text">
                      {{ dominantAgeGroup }} is je grootste doelgroep.
                    </div>
                  </div>
                </div>
              </b-col>

              <b-col md="4" class="mb-3">
                <div class="insight-card">
                  <div class="insight-icon">📈</div>
                  <div>
                    <div class="insight-title">Terugkerende klanten</div>
                    <div class="insight-text">
                      {{ returningCustomers }}% van je klanten keert terug.
                    </div>
                  </div>
                </div>
              </b-col>

              <b-col md="4" class="mb-3">
                <div class="insight-card">
                  <div class="insight-icon">⏰</div>
                  <div>
                    <div class="insight-title">Beste verkoopmoment</div>
                    <div class="insight-text">
                      Meeste aankopen rond {{ peakHour }}.
                    </div>
                  </div>
                </div>
              </b-col>
            </b-row>
          </b-card-body>
        </b-card>

      </div>
    </b-container>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  metaInfo: { title: 'Klantstatistieken' },

  data() {
    return {
      loading: false,
      selectedRange: 365,
      stats: null,
      ranges: [
        { label: '7 dagen', value: 7 },
        { label: '30 dagen', value: 30 },
        { label: '90 dagen', value: 90 },
        { label: '1 jaar', value: 365 },
        { label: '2 jaar', value: 730 },
        { label: '3 jaar', value: 1095 },
        { label: '6 jaar', value: 2190 },
      ]
    }
  },

  created() {
    this.fetchStats();
  },

  watch: {
    selectedRange() {
      this.fetchStats();
    }
  },

  methods: {
    fetchStats() {
      this.loading = true;

      axios.get('https://api.tixgo.nl/customers/stats', {
        params: { range: this.selectedRange }
      })
      .then(res => {
        this.stats = res.data;
      })
      .finally(() => {
        this.loading = false;
      });
    }
  },

  computed: {

    kpis() {
      if (!this.stats) return [];

      return [
        {
          label: 'Unieke klanten',
          value: this.stats.kpis.unique_customers
        },
        {
          label: 'Omzet',
          value: '€' + this.stats.kpis.revenue
        },
        {
          label: 'Gem. orderwaarde',
          value: '€' + this.stats.kpis.aov
        },
        {
          label: 'Terugkerend',
          value: this.stats.kpis.returning_percentage + '%'
        }
      ];
    },

    hourlySales() {
        if (!this.stats || !Array.isArray(this.stats.hourly)) {
            return [];
        }

        const values = this.stats.hourly;

        if (!values.length) {
            return [];
        }

        const max = Math.max(...values);

        return values.map((count, index) => ({
            label: index + 'u',
            percent: max > 0 ? (count / max) * 100 : 0
        }));
    },

    ageStats() {
      if (!this.stats) return [];

      return Object.keys(this.stats.age_distribution).map(key => ({
        label: key,
        percent: this.stats.age_distribution[key]
      }));
    },

    genderStats() {
        if (!this.stats || !this.stats.gender_distribution) return [];

        const total = this.stats.gender_distribution.reduce(
            (sum, g) => sum + parseInt(g.total),
            0
        );

        const labelMap = {
            male: 'Man',
            female: 'Vrouw',
            other: 'Anders'
        };

        return this.stats.gender_distribution.map(g => {

            let label = g.gender;

            if (!label || label === '') {
            label = 'Zeg ik liever niet';
            } else {
            label = labelMap[label] ?? label;
            }

            return {
            label,
            percent: total > 0
                ? Math.round((parseInt(g.total) / total) * 100)
                : 0
            };
        });
    },

    dominantAgeGroup() {
      if (!this.ageStats.length) return '-';

      return this.ageStats.reduce((max, item) =>
        item.percent > max.percent ? item : max
      ).label;
    },

    returningCustomers() {
      return this.stats?.kpis.returning_percentage ?? 0;
    },

    peakHour() {
      if (!this.hourlySales.length) return '-';

      return this.hourlySales.reduce((max, item) =>
        item.percent > max.percent ? item : max
      ).label;
    }

  }
}
</script>

<style scoped>
.kpi-card {
  border-radius: 12px;
  transition: all 0.2s ease;
}
.kpi-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 10px 25px rgba(0,0,0,0.08);
}

.mini-bar-chart {
  display: flex;
  align-items: flex-end;
  gap: 20px;
  height: 200px;
}

.mini-bar {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  height:100%;
}

.mini-bar-fill {
  width: 100%;
  background: linear-gradient(
    180deg,
    #e24b73 0%,
    #ca355b 50%,
    #a72647 100%
  );
  border-radius: 6px 6px 0 0;
  min-height:4px;
}

.mini-bar-label {
  margin-top: 8px;
  font-size: 14px;
  color: #6c757d;
}

.insight-card {
  display: flex;
  gap: 12px;
  padding: 16px;
  border-radius: 12px;
  background: #f8f9fa;
  transition: 0.2s ease;
}

.insight-card:hover {
  background: #f1f3f5;
  transform: translateY(-3px);
}

.insight-icon {
  font-size: 24px;
}

.insight-title {
  font-weight: 600;
  font-size: 14px;
  color: #6c757d;
  margin-bottom: 4px;
}

.insight-text {
  font-weight: 500;
}

</style>

<style>
    .progress-bar {
    background: linear-gradient(
        90deg,
        #e24b73 0%,
        #ca355b 50%,
        #a72647 100%
    ) !important;
    }
</style>